import React, { useEffect, useRef, forwardRef, useImperativeHandle, useState } from "react";
import { env } from "../controllers/environment.js";

const SmartUploadWidget = forwardRef(({ urn, trackId, buttonId, wrapperConfig }, ref) => {
  const containerRef = useRef(null);
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [containerKey, setContainerKey] = useState(0);
  const [diroStatusResponse, setDiroStatusResponse] = useState(null);
  const hasInitialized = useRef(false);
  const scriptRef = useRef(null);

  // Default wrapper configuration matching the HTML example
  const defaultWrapper = {
    height: "300px",
    width: "350px",
    themeColor: "black",
    fontFamily: "sans-serif",
    fontSizeTitle: "12px",
    fontWeightTitle: "400",
    buttonHeight: "30px",
  };

  useImperativeHandle(ref, () => ({
    updateWidget: (data) => {
      // Widget will reinitialize automatically when URN changes
      // console.log("Widget updated with data:", data);
    },
    reinitialize: () => {
      // console.log("Reinitializing Bank widget...");
      // Clear any existing widget instances
      cleanupWidget();
      setContainerKey((prev) => prev + 1);
      hasInitialized.current = false;
      setIsWidgetLoaded(false);
      setHasError(false);
      setTimeout(() => {
        initializeWidget();
      }, 200);
    },
    cleanup: () => {
      cleanupWidget();
    },
    getDiroStatusResponse: () => {
      return diroStatusResponse;
    },
  }));

  const cleanupWidget = () => {
    // console.log("Cleaning up Bank widget...");

    // Clear container
    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }

    // Clear any existing reactWidget elements in the document
    const existingWidgets = document.querySelectorAll("#reactWidget");
    existingWidgets.forEach((widget) => {
      if (widget.parentNode) {
        widget.parentNode.removeChild(widget);
      }
    });

    // Reset widget state
    setIsWidgetLoaded(false);
    setHasError(false);
    hasInitialized.current = false;

    // Clear any widget-related global variables
    if (window.DiroWidget) {
      try {
        // Try to cleanup any existing widget instances
        if (window.DiroWidget.cleanup) {
          window.DiroWidget.cleanup();
        }
      } catch (e) {
        console.log("Widget cleanup method not available:", e.message);
      }
    }
  };

  const callDiroStatusAPI = async (buttonId) => {
    try {
      // console.log("Calling dirostatus API with buttonId:", buttonId);
      const response = await fetch(env.dirostatus, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          buttonid: buttonId,
          btnDetails: true,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // console.log("Dirostatus API response:", data);
        setDiroStatusResponse(data);
        return data;
      } else {
        console.error("Dirostatus API error:", response.status, response.statusText);
        return null;
      }
    } catch (error) {
      console.error("Failed to call dirostatus API:", error);
      return null;
    }
  };

  const loadCSS = () => {
    return new Promise((resolve) => {
      // Check if CSS is already loaded
      if (document.querySelector(`link[href="${env.uploadCDN_CSS}"]`)) {
        resolve();
        return;
      }

      const cssLink = document.createElement("link");
      cssLink.rel = "stylesheet";
      cssLink.href = env.uploadCDN_CSS;
      cssLink.onload = () => resolve();
      cssLink.onerror = () => {
        console.error("Failed to load Diro widget CSS");
        resolve(); // Resolve anyway to continue
      };
      document.head.appendChild(cssLink);
    });
  };

  const initializeWidget = async () => {
    if (hasInitialized.current) return;

    try {
      const finalButtonId = buttonId;
      // console.log("Initializing Bank widget with buttonId:", finalButtonId);
      // console.log("Track ID:", trackId || urn || "");

      setHasError(false);

      // Call dirostatus API with button ID
      await callDiroStatusAPI(finalButtonId);

      // Clear any existing widgets first
      cleanupWidget();

      // Load CSS first
      await loadCSS();

      // Create the widget div immediately
      createWidgetDiv();

      // Then load the JS script with timeout
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Widget loading timeout")), 10000));

      await Promise.race([loadJS(), timeoutPromise]);

      setIsWidgetLoaded(true);
      hasInitialized.current = true;
      console.log("Bank widget initialized successfully");
    } catch (error) {
      console.error("Failed to initialize Bank widget:", error);
      console.error("Error details:", {
        buttonId: buttonId || "O.DE-dc9ylq-SKgo-sandbox",
        trackId: trackId || urn || "",
        errorMessage: error.message,
        stack: error.stack,
      });
      setHasError(true);
    }
  };

  const createWidgetDiv = () => {
    if (!containerRef.current) return;

    // Clear any existing content in container
    containerRef.current.innerHTML = "";

    // Also clear any existing reactWidget elements globally to prevent conflicts
    const existingWidget = document.getElementById("reactWidget");
    if (existingWidget && existingWidget.parentNode) {
      existingWidget.parentNode.removeChild(existingWidget);
    }

    // Create the widget div with required attributes matching the HTML structure
    const widgetDiv = document.createElement("div");
    widgetDiv.id = "reactWidget"; // Use the exact ID from the HTML example

    // Use the new button ID as specified
    const finalButtonId = buttonId || "O.DE-dc9ylq-SKgo-sandbox";
    widgetDiv.setAttribute("data-buttonid", finalButtonId);
    widgetDiv.setAttribute("data-trackid", trackId || urn || "");

    // Merge wrapper configuration and format exactly like the HTML
    const finalWrapper = { ...defaultWrapper, ...wrapperConfig };
    widgetDiv.setAttribute("wrapper", JSON.stringify(finalWrapper));

    // console.log("Created widget div with attributes:", {
    //   id: widgetDiv.id,
    //   buttonId: finalButtonId,
    //   trackId: trackId || urn || "",
    //   wrapper: finalWrapper,
    // });

    // Append the widget div to the container
    containerRef.current.appendChild(widgetDiv);

    // Set up mutation observer to detect when widget content appears
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if the widget has been populated with content
          const hasContent =
            widgetDiv.children.length > 0 ||
            widgetDiv.innerHTML.trim().length > 0 ||
            widgetDiv.querySelector("iframe, form, input, button");

          if (hasContent) {
            // console.log("Widget content detected, marking as loaded");
            setIsWidgetLoaded(true);
            observer.disconnect();
          }
        }
      });
    });

    // Start observing the widget div for changes
    observer.observe(widgetDiv, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    // Trigger any available initialization methods
    setTimeout(() => {
      // console.log("Attempting to trigger widget initialization...");

      // Try various initialization methods
      const initMethods = [
        () => window.DiroWidget && window.DiroWidget.init && window.DiroWidget.init(),
        () => window.DiroWidget && typeof window.DiroWidget === "function" && window.DiroWidget(),
        () => window.initializeDiroWidgets && window.initializeDiroWidgets(),
        () => {
          // Dispatch a custom event that the script might listen for
          const event = new Event("DOMContentLoaded");
          document.dispatchEvent(event);
        },
        () => {
          // Try to trigger mutation observer on document
          const event = new CustomEvent("diro-widget-ready", { detail: { widgetId: widgetDiv.id } });
          document.dispatchEvent(event);
        },
      ];

      // initMethods.forEach((method, index) => {
      //   try {
      //     console.log(`Trying initialization method ${index + 1}...`);
      //     method();
      //   } catch (e) {
      //     console.log(`Initialization method ${index + 1} failed:`, e.message);
      //   }
      // });

      // Fallback: mark as loaded after 5 seconds if nothing happens
      setTimeout(() => {
        if (!isWidgetLoaded) {
          // console.log("Widget auto-detection timeout, assuming it's working...");
          setIsWidgetLoaded(true);
          observer.disconnect();
        }
      }, 5000);
    }, 500);
  };

  const loadJS = () => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded (look for existing script tags)
      const existingScript = document.querySelector(`script[src="${env.uploadCDN_JS}"]`);
      if (existingScript) {
        // console.log("Diro script already exists, proceeding...");
        // Wait a bit for the script to initialize if it was just added
        setTimeout(() => resolve(), 100);
        return;
      }

      const script = document.createElement("script");
      script.src = env.uploadCDN_JS;
      script.async = true;

      script.onload = () => {
        console.log("Diro script loaded successfully");
        // Give the script time to initialize and scan the DOM
        setTimeout(() => {
          console.log("Diro script initialization complete");
          resolve();
        }, 500);
      };

      script.onerror = (event) => {
        console.error("Failed to load Diro widget script", event);
        reject(new Error("Failed to load widget script"));
      };

      scriptRef.current = script;
      document.head.appendChild(script);
      console.log("Diro script added to document head");
    });
  };

  useEffect(() => {
    // Initialize widget after a small delay to ensure DOM is ready and avoid conflicts
    const timeoutId = setTimeout(() => {
      initializeWidget();
    }, 100); // Increased delay for better stability

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      // Clean up widget when component unmounts
      cleanupWidget();
    };
  }, [containerKey, urn, buttonId]);

  return (
    <div className="smart-upload-widget">
      {/* {!isWidgetLoaded && !hasError && (
        <div className="widget-loading" style={{ padding: '16px', textAlign: 'center' }}>
          Loading Diro widget...
        </div>
      )} */}

      {hasError && (
        <div className="widget-error" style={{ padding: "16px", textAlign: "center", color: "#dc3545" }}>
          Failed to load upload widget. Please refresh the page and try again.
          <br />
          <button
            onClick={() => {
              setHasError(false);
              setIsWidgetLoaded(false);
              hasInitialized.current = false;
              setContainerKey((prev) => prev + 1);
              setTimeout(initializeWidget, 100);
            }}
            style={{
              marginTop: "8px",
              padding: "4px 8px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}> */}
      <div key={`smart-upload-widget-${containerKey}`} ref={containerRef} className="upload-widget-container" />
      {/* </div> */}
    </div>
  );
});

SmartUploadWidget.displayName = "SmartUploadWidget";

export default SmartUploadWidget;
